// src/pages/merchant/Notifications.js — Marchand : centre de notifications
// Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheck, FiCheckCircle, FiTrash2, FiRefreshCw, FiShoppingBag, FiDollarSign, FiUsers, FiCreditCard, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { notifAPI } from '../../services/api';

const TYPE_ICON = {
  order: FiShoppingBag,
  payment: FiDollarSign,
  lead: FiUsers,
  subscription: FiCreditCard
};

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 30) return `il y a ${j} j`;
  return new Date(d).toLocaleDateString('fr-FR');
};

export default function MerchantNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notifAPI.list({ limit: 100 });
      const arr = Array.isArray(data?.data) ? data.data : [];
      setItems(arr);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    try { await notifAPI.markRead(n.id); } catch (e) {}
  };

  // Destination de navigation selon le type / les données de la notification
  const resolveLink = (n) => {
    const d = n.data || {};
    if (d.link || d.url) return d.link || d.url;
    const orderId = d.orderId;
    const convId = d.conversationId;
    switch (n.type) {
      case 'subscription': return '/merchant/subscription';
      case 'order':
      case 'payment': return orderId ? `/merchant/orders?order=${orderId}` : '/merchant/orders';
      case 'message': return convId ? `/merchant/messages?c=${convId}` : '/merchant/messages';
      case 'lead': return '/merchant/leads';
      case 'withdrawal': return '/merchant/wallet';
      case 'ticket':
      case 'support': return '/merchant/tickets';
      case 'kyc_submit':
      case 'kyc_reject':
      case 'kyc_approve': return '/merchant/kyc';
      case 'deal':
      case 'review': return '/merchant/deals';
      default: return null;
    }
  };

  const openNotification = (n) => {
    markRead(n);
    const link = resolveLink(n);
    if (!link) return;
    if (/^https?:\/\//i.test(link)) window.open(link, '_blank', 'noopener');
    else navigate(link);
  };

  const markAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    try { await notifAPI.markAllRead(); toast.success('Tout marqué comme lu'); } catch (e) {}
  };

  const remove = async (n) => {
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    try { await notifAPI.remove(n.id); } catch (e) {}
  };

  const shown = filter === 'unread' ? items.filter((n) => !n.read) : items;
  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <FiBell className="text-[#e11d7b]" size={24} />
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unread > 0 && <span className="bg-[#e11d7b] text-white text-xs font-bold px-2 py-0.5 rounded-full">{unread}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={markAll} disabled={!unread} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
            <FiCheckCircle size={15} /> Tout lire
          </button>
          <button onClick={load} className="p-2 border rounded-lg hover:bg-gray-50" title="Actualiser"><FiRefreshCw size={16} /></button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[{ k: 'all', l: 'Toutes' }, { k: 'unread', l: 'Non lues' }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === f.k ? 'bg-[#e11d7b] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e11d7b]" /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl shadow-sm">
          <FiBell className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-600">Aucune notification.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((n) => {
            const Icon = TYPE_ICON[n.type] || FiBell;
            return (
              <div key={n.id} onClick={() => openNotification(n)}
                className={`group flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${n.read ? 'bg-white border-gray-100 hover:border-gray-200' : 'bg-pink-50 border-pink-200 hover:border-pink-300'}`}>
                <div className={`mt-0.5 p-2 rounded-lg ${n.read ? 'bg-gray-100 text-gray-500' : 'bg-[#e11d7b] text-white'}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm ${n.read ? 'font-medium text-gray-800' : 'font-bold text-gray-900'}`}>{n.title || 'Notification'}</p>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#e11d7b]" />}
                  </div>
                  {n.message && <p className="text-sm text-gray-600 mt-0.5">{n.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                </div>
                <div className="flex items-center gap-1">
                  {resolveLink(n) && <FiChevronRight className="text-gray-300 group-hover:text-[#e11d7b] transition" size={16} />}
                  {!n.read && (
                    <button onClick={(e) => { e.stopPropagation(); markRead(n); }} className="p-1.5 text-gray-400 hover:text-green-600" title="Marquer lu"><FiCheck size={15} /></button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); remove(n); }} className="p-1.5 text-gray-400 hover:text-red-600" title="Supprimer"><FiTrash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-center text-xs text-gray-400 mt-8">Maxula — contact@maxula.ma</p>
    </div>
  );
}
